import { OnboardingDraftV1Schema, type OnboardingDraftV1 } from "@repo/types";
import {
  getOnboardingV1StepIndex,
  type OnboardingV1StepId,
} from "./onboarding-v1-step-search";

export const ONBOARDING_V1_DRAFT_STORAGE_KEY =
  "glider_onboarding_v1_draft" as const;
export const ONBOARDING_V1_DRAFT_RETENTION_MS = 7 * 24 * 60 * 60 * 1_000;

export interface OnboardingV1DraftStorage {
  getItem(key: string): string | null;
  removeItem(key: string): void;
  setItem(key: string, value: string): void;
}

function getBrowserStorage(): OnboardingV1DraftStorage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

export function createOnboardingV1Draft(now = new Date()): OnboardingDraftV1 {
  return {
    interests: [],
    interestsUnknown: false,
    selectedBlueprintId: null,
    selectedNetwork: "EVM",
    selfDescriptions: [],
    updatedAt: now.toISOString(),
    username: null,
    version: 1,
  };
}

export function clearOnboardingV1Draft(
  storage: OnboardingV1DraftStorage | null = getBrowserStorage(),
): void {
  try {
    storage?.removeItem(ONBOARDING_V1_DRAFT_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in privacy-constrained browser contexts.
  }
}

export function readOnboardingV1Draft(
  storage: OnboardingV1DraftStorage | null = getBrowserStorage(),
  now = new Date(),
): OnboardingDraftV1 | null {
  try {
    const serialized = storage?.getItem(ONBOARDING_V1_DRAFT_STORAGE_KEY);
    if (!serialized) {
      return null;
    }

    const parsed = OnboardingDraftV1Schema.safeParse(JSON.parse(serialized));
    if (
      !parsed.success ||
      now.getTime() - new Date(parsed.data.updatedAt).getTime() >
        ONBOARDING_V1_DRAFT_RETENTION_MS
    ) {
      clearOnboardingV1Draft(storage);
      return null;
    }

    return parsed.data;
  } catch {
    clearOnboardingV1Draft(storage);
    return null;
  }
}

export function writeOnboardingV1Draft(
  draft: OnboardingDraftV1,
  storage: OnboardingV1DraftStorage | null = getBrowserStorage(),
): boolean {
  const parsed = OnboardingDraftV1Schema.safeParse(draft);
  if (!parsed.success || !storage) {
    return false;
  }

  try {
    storage.setItem(
      ONBOARDING_V1_DRAFT_STORAGE_KEY,
      JSON.stringify(parsed.data),
    );
    return true;
  } catch {
    return false;
  }
}

function getEarliestIncompleteStep(
  draft: OnboardingDraftV1,
): Exclude<OnboardingV1StepId, "hook" | "projection" | "fund"> {
  if (!draft.username) {
    return "name";
  }
  if (draft.selfDescriptions.length === 0) {
    return "profile";
  }
  if (draft.interests.length === 0 && !draft.interestsUnknown) {
    return "interests";
  }
  if (!draft.selectedBlueprintId) {
    return "pick";
  }
  return "account";
}

export function resolveOnboardingV1DraftStep(
  requestedStep: OnboardingV1StepId,
  draft: OnboardingDraftV1 | null,
): OnboardingV1StepId {
  if (!draft) {
    return "hook";
  }

  const earliestIncompleteStep = getEarliestIncompleteStep(draft);
  if (requestedStep === "hook") {
    return earliestIncompleteStep;
  }

  return getOnboardingV1StepIndex(requestedStep) <=
    getOnboardingV1StepIndex(earliestIncompleteStep)
    ? requestedStep
    : earliestIncompleteStep;
}
